import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';

export const Route = createFileRoute('/history')({ component: HistoryPage });

interface DayAgg { date: string; count: number; energy: number; xp: number }
interface MonthResp {
  month: string;
  days: DayAgg[];
  totals: { count: number; energy: number; xp: number };
}
interface DayDetail {
  date: string;
  completed: {
    id: string; title: string; energy: number; xp: number;
    completedAt: string | null; kind: 'habit' | 'task';
  }[];
  activeAchievements: { id: string; name: string; type: string; currentLevel: number }[];
}

function HistoryPage() {
  const [refDate, setRefDate] = useState(() => new Date());
  const [selected, setSelected] = useState<string | null>(null);

  const monthStr = `${refDate.getFullYear()}-${String(refDate.getMonth() + 1).padStart(2, '0')}`;
  const month = useQuery({
    queryKey: ['history', monthStr],
    queryFn: () => api<MonthResp>(`/api/history?month=${monthStr}`),
  });
  const day = useQuery({
    queryKey: ['history-day', selected],
    queryFn: () => api<DayDetail>(`/api/history/day?date=${selected}`),
    enabled: !!selected,
  });

  const days = useMemo(() => buildMonthGrid(refDate), [refDate]);
  const dayMap = useMemo(() => {
    const m = new Map<string, DayAgg>();
    (month.data?.days ?? []).forEach((d) => m.set(d.date, d));
    return m;
  }, [month.data]);

  const maxEnergy = Math.max(1, ...Array.from(dayMap.values()).map((d) => d.energy));

  function shift(delta: number) {
    const d = new Date(refDate);
    d.setMonth(d.getMonth() + delta);
    setRefDate(d);
    setSelected(null);
  }

  const totals = month.data?.totals ?? { count: 0, energy: 0, xp: 0 };

  return (
    <div className="mx-auto max-w-7xl px-6 py-6">
      <h1 className="mb-4 text-2xl font-bold text-neutral-100">History</h1>

      <section className="mb-6 grid grid-cols-3 gap-3">
        <Stat label="Toplam XP" value={String(totals.xp)} tint="brand" />
        <Stat label="Tamamlanan" value={String(totals.count)} tint="task" />
        <Stat label="Biriken Enerji" value={`⚡ ${totals.energy}`} tint="time" />
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.3fr_1fr]">
        <section className="rounded-xl border border-border-soft bg-bg-raised p-4">
          <div className="mb-3 flex items-center justify-between">
            <button onClick={() => shift(-1)}
                    className="rounded px-2 py-1 text-sm text-neutral-400 hover:bg-bg-elevated">‹</button>
            <h2 className="text-sm font-semibold text-neutral-200">
              {refDate.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
            </h2>
            <button onClick={() => shift(1)}
                    className="rounded px-2 py-1 text-sm text-neutral-400 hover:bg-bg-elevated">›</button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-neutral-500">
            {['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'].map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {days.map((d, i) => {
              if (!d) return <div key={i} className="aspect-square" />;
              const agg = dayMap.get(d);
              const intensity = agg ? agg.energy / maxEnergy : 0;
              const bg = agg && agg.count > 0
                ? `rgba(139, 92, 246, ${0.25 + intensity * 0.6})`
                : undefined;
              return (
                <button
                  key={i}
                  onClick={() => setSelected(d)}
                  className={`relative flex aspect-square flex-col items-center justify-center rounded-md text-xs transition ${
                    selected === d ? 'ring-2 ring-brand-400' : ''
                  } ${agg ? 'text-neutral-100' : 'text-neutral-500 bg-bg-elevated hover:bg-bg-elevated/70'}`}
                  style={bg ? { background: bg } : undefined}
                >
                  <span>{Number(d.slice(-2))}</span>
                  {agg && agg.count > 0 && (
                    <span className="font-pixel text-[8px] text-neutral-200/90">
                      {agg.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        <section className="rounded-xl border border-border-soft bg-bg-raised p-4">
          <h2 className="mb-3 text-sm font-semibold text-neutral-200">
            {selected ? `${formatDate(selected)} detayı` : 'Bir gün seç'}
          </h2>
          {!selected && <p className="text-sm text-neutral-500">Takvimden bir güne tıkla.</p>}
          {selected && day.isLoading && <p className="text-sm text-neutral-500">Yükleniyor…</p>}
          {day.data && <DayDetail detail={day.data} />}
        </section>
      </div>
    </div>
  );
}

function DayDetail({ detail }: { detail: DayDetail }) {
  const habits = detail.completed.filter((t) => t.kind === 'habit');
  const tasks = detail.completed.filter((t) => t.kind === 'task');
  const totalXp = detail.completed.reduce((s, t) => s + t.xp, 0);
  const totalEnergy = detail.completed.reduce((s, t) => s + t.energy, 0);

  return (
    <div className="space-y-4 text-sm">
      <div className="flex gap-3 text-[11px] text-neutral-400">
        <span>✔ <b className="font-pixel text-neutral-100">{detail.completed.length}</b> tamamlanan</span>
        <span>⭐ <b className="font-pixel text-xp-bar">{totalXp}</b> xp</span>
        <span>⚡ <b className="font-pixel text-time-400">{totalEnergy}</b> enerji</span>
      </div>

      <Subsection title="Alışkanlıklar" items={habits} accent="habit" />
      <Subsection title="Günlük görevler" items={tasks} accent="task" />

      {detail.activeAchievements.length > 0 && (
        <div>
          <h3 className="mb-1 text-xs font-semibold text-ach-400">O günkü aktif achievement'lar</h3>
          <ul className="space-y-1">
            {detail.activeAchievements.map((a) => (
              <li key={a.id} className="rounded-md bg-bg-elevated/50 px-3 py-1.5 text-xs">
                🏆 {a.name} {a.type === 'recurring' && <span className="text-neutral-500">· Lv {a.currentLevel}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Subsection({ title, items, accent }: {
  title: string;
  items: { id: string; title: string; energy: number; xp: number }[];
  accent: 'habit' | 'task';
}) {
  const color = accent === 'habit' ? 'text-habit-400' : 'text-task-400';
  if (items.length === 0) return null;
  return (
    <div>
      <h3 className={`mb-1 text-xs font-semibold ${color}`}>{title}</h3>
      <ul className="space-y-1">
        {items.map((t) => (
          <li key={t.id} className="flex items-center justify-between rounded-md bg-bg-elevated/50 px-3 py-1.5 text-xs">
            <span className="truncate text-neutral-200">{t.title}</span>
            <span className="shrink-0 font-pixel text-[10px] text-neutral-500">
              ⚡{t.energy} · +{t.xp}xp
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stat({ label, value, tint }: { label: string; value: string; tint: 'brand' | 'task' | 'time' }) {
  const cls = {
    brand: 'text-brand-400 border-brand-500/40',
    task: 'text-task-400 border-task-500/40',
    time: 'text-time-400 border-time-500/40',
  }[tint];
  return (
    <div className={`rounded-xl border bg-bg-raised p-4 ${cls}`}>
      <div className="text-[11px] uppercase tracking-wider text-neutral-400">{label}</div>
      <div className="mt-1 font-pixel text-xl">{value}</div>
    </div>
  );
}

function formatDate(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' });
}

function buildMonthGrid(ref: Date): (string | null)[] {
  const year = ref.getFullYear();
  const month = ref.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const offset = (first.getDay() + 6) % 7;
  const cells: (string | null)[] = Array(offset).fill(null);
  for (let d = 1; d <= last.getDate(); d++) {
    cells.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
